import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from 'context/AuthContext';
import { buscarIniciativasPorPlayer } from 'services/iniciativa.service';
import { registrarAcompanhar } from 'services/iniciativa-action.service';
import {
  buildRankingPontuado,
  buscarIndicacoesExistentes,
  buscarRankingParticipantes,
  isIniciativaExecutivoOnly,
  RankingPontuacao,
} from 'services/indicacao-ge.service';
import { IniciativaFormatada } from 'types/iniciativa';
import { Header } from 'pages/HomePublic/components/Header';
import { IniciativaCardListagem } from './components/IniciativaCardListagem';
import { FiCalendar } from 'react-icons/fi';
import { useControlDate } from 'hooks/useControlDate';
import { PHASE_IDS } from 'utils/constants';
import { clearRegistrationUser } from 'utils/fns';
type RankingMap = Map<string, RankingPontuacao>;

export function ListagemInscricaoPremio() {
  const { user } = useAuth();
  const location = useLocation();
  const [todasIniciativas, setTodasIniciativas] = useState<IniciativaFormatada[]>([]);
  const [indicacoesGE, setIndicacoesGE] = useState<Map<string, any>>(new Map());
  const [rankingsGE, setRankingsGE] = useState<Map<string, RankingMap>>(new Map());
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState<'disponiveis' | 'inscritas'>('disponiveis');
  const [reloadKey, setReloadKey] = useState(0);
  const [acompanharRegistrado, setAcompanharRegistrado] = useState(false);
  const periodoCadastro = useControlDate(PHASE_IDS.CONEXAO_REGISTRATION_PERIOD);

  const { iniciativas, iniciativasGE } = useMemo(() => {
    const proprias: IniciativaFormatada[] = [];
    const ge: IniciativaFormatada[] = [];
    todasIniciativas.forEach((ini) => {
      if (
        user?._id &&
        ini.status === 'inscrita' &&
        isIniciativaExecutivoOnly(ini.iniciativaOriginal, user._id) &&
        (ini.participantes?.length || 0) > 5
      ) {
        ge.push(ini);
      } else if (
        !(user?._id && isIniciativaExecutivoOnly(ini.iniciativaOriginal, user._id))
      ) {
        proprias.push(ini);
      }
    });
    return { iniciativas: proprias, iniciativasGE: ge };
  }, [todasIniciativas, user?._id]);

  const disponiveis = iniciativas.filter(ini => ini.status === "aprovada" || ini.status === "inscrita");
  const inscritas = iniciativas.filter(ini => ini.status === "inscrita");

  const premiadas = iniciativas.filter(
    (ini) => ini.classificadaEtapa1 || ini.classificadaEtapa2 || ini.position
  );

  const counterDisponiveis = `${inscritas.length}/${iniciativas.length} inscritas`;
  const counterInscritas = `${inscritas.length}/${iniciativas.length} confirmada(s) participação`;
  const counterPremiadas = `${premiadas.length}/${iniciativas.length} premiada${premiadas.length !== 1 ? 's' : ''}`;

  const listaExibida = filtro === 'disponiveis' ? disponiveis : inscritas;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location]);

  useEffect(() => {
    if (!user?._id) return;

    async function carregar() {
      try {
        setLoading(true);
        const minhas = await buscarIniciativasPorPlayer(user!._id);
        setTodasIniciativas(minhas);

        const geInscritas = minhas.filter(
          (ini) =>
            ini.status === 'inscrita' &&
            isIniciativaExecutivoOnly(ini.iniciativaOriginal, user!._id) &&
            (ini.participantes?.length || 0) > 5,
        );

        if (geInscritas.length > 0) {
          const ids = geInscritas.map((ini) => ini.id);
          const [indicacoesMap, rankingsData] = await Promise.all([
            buscarIndicacoesExistentes(ids).catch(() => new Map<string, any>()),
            Promise.all(
              geInscritas.map(async (ini) => {
                const ranking = await buscarRankingParticipantes(ini.id).catch(
                  () => [] as Array<{ player: string; registration?: string; total: number }>,
                );
                return [ini.id, ranking] as const;
              }),
            ),
          ]);
          const rankings = rankingsData.map(([iniId, ranking]) => {
            const rMap = buildRankingPontuado(ranking, indicacoesMap.get(iniId));
            return [iniId, rMap] as const;
          });
          setIndicacoesGE(indicacoesMap);
          setRankingsGE(new Map(rankings));
        } else {
          setIndicacoesGE(new Map());
          setRankingsGE(new Map());
        }
      } catch {
        setTodasIniciativas([]);
        setIndicacoesGE(new Map());
        setRankingsGE(new Map());
      } finally {
        setLoading(false);
      }
    }

    carregar();
  }, [user, reloadKey]);

  useEffect(() => {
    if (!user?._id || loading || acompanharRegistrado) return;
    const playerId = user._id;
    const currentPlayer = clearRegistrationUser(playerId);

    const possuiParticipacaoConfirmada = iniciativas.some((ini) => {
      const confirmedPlayers: string[] = (ini.iniciativaOriginal as any)?.confirmedPlayers || [];
      return (
        ini.status === 'inscrita' &&
        confirmedPlayers.some((cp) => clearRegistrationUser(cp) === currentPlayer)
      );
    });

    if (!possuiParticipacaoConfirmada) return;

    async function registrarAcompanhamento() {
      try {
        await registrarAcompanhar(playerId);
        setAcompanharRegistrado(true);
      } catch (actionLogError) {
        console.warn('Falha ao registrar action_log acompanhar:', actionLogError);
      }
    }

    registrarAcompanhamento();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id, loading, acompanharRegistrado, iniciativas]);

  return (
    <div className="min-h-screen bg-gray-100">
      <Header currentPage="minhas-iniciativas" />

      {/* Hero banner */}
      <div className="bg-blue-bb py-10 pb-12">
        <div className="mx-auto max-w-7xl px-4">
          <h1 className="text-3xl font-bold text-pink-bb m-0 mb-2.5 leading-snug">
            Minhas iniciativas no<span className='text-yellow-bb'> Prêmio BB 2026</span>
          </h1>
          <p className="text-2xl text-green-bb-1 m-0 font-bold mb-1">
            Confira aqui todas as suas iniciativas
          </p>
          <p className="text-lg text-white/90 m-0">
            Confirme a participação nas Iniciativas abaixo.
          </p>

            <div className="flex flex-col sm:flex-row items-start gap-1 text-yellow-bb py-2 text-sm sm:text-base">
              <FiCalendar className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
              <span className="font-semibold">
                PERÍODO PARA CONFIRMAÇÃO DE PARTICIPAÇÃO NAS INICIATIVAS: DE 16/04 A 20/04
              </span>
          </div>
        </div>
      </div>

      {/* Barra de filtro */}
      {!loading && iniciativas.length > 0 && (
        <div className="bg-white border-b border-gray-200">
          <div className="mx-auto max-w-7xl px-4 py-3 flex items-center justify-between gap-4">
            <div className="flex flex-col">
              <span className="text-sm font-semibold text-blue-bb">
                {filtro === 'disponiveis' ? counterDisponiveis : counterInscritas}
              </span>
              <span className="text-sm font-semibold text-blue-bb">
                {counterPremiadas}
              </span>
            </div>
            <select
              value={filtro}
              onChange={(e) => setFiltro(e.target.value as 'disponiveis' | 'inscritas')}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-bb"
            >
              <option value="disponiveis">Iniciativas disponíveis</option>
              <option value="inscritas">Iniciativas já inscritas</option>
            </select>
          </div>
        </div>
      )}

      <main className="px-4 py-10 pb-20">
        <div className="mx-auto max-w-7xl">
          {loading ? (
            <div className="text-center py-16 text-gray-500 text-base">
              <span>Carregando iniciativas...</span>
            </div>
          ) : iniciativas.length === 0 && iniciativasGE.length === 0 ? (
            <div className="text-center py-16 text-gray-500 text-base">
              <p>Nenhuma iniciativa para inscrição no momento.</p>
            </div>
          ) : (
            <div className="flex flex-col gap-12">
              {iniciativasGE.length > 0 && (
                <section className="flex flex-col gap-5">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-bb-1 font-title m-0 mb-1">
                      Votações pendentes como Gestor Executivo
                    </h2>
                    <p className="text-base text-gray-600 m-0">
                      Vote nos 5 participantes que mais atuaram para o sucesso da entrega.
                    </p>
                  </div>
                  <div className="flex flex-col gap-8">
                    {iniciativasGE.map((ini) => (
                      <IniciativaCardListagem
                        key={`ge-${ini.id}`}
                        {...ini}
                        modoGE={true}
                        indicacaoExistente={indicacoesGE.get(ini.id) || null}
                        rankingMap={rankingsGE.get(ini.id)}
                        onConfirm={() => setReloadKey(k => k + 1)}
                      />
                    ))}
                  </div>
                </section>
              )}

              {iniciativas.length > 0 && (
                <section className="flex flex-col gap-5">
                  {listaExibida.length === 0 ? (
                    <div className="text-center py-16 text-gray-500 text-base">
                      <p>
                        {filtro === 'disponiveis'
                          ? 'Nenhuma iniciativa disponível para inscrição.'
                          : 'Nenhuma iniciativa inscrita ainda.'}
                      </p>
                    </div>
                  ) : (
                    listaExibida.map((ini) => (
                      <IniciativaCardListagem key={ini.id} {...ini} onConfirm={() => setReloadKey(k => k + 1)} />
                    ))
                  )}
                </section>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
